/**
 * EVENTSFLO-ADMIN-VENUE-SHARE-COPY-UI-V1
 *
 * Admin-only dialogs for venue visibility actions:
 *   - VenueVisibilityBadge: tag rendered on each venue row
 *   - VenueShareDialog: share a venue with selected clients (same canonical IDs)
 *   - VenueCopyToClientDialog: copy a venue + its locations to a target client
 *     as an editable client-specific template (new IDs)
 *
 * Talks to:
 *   PATCH /api/venues/{id}/client-sharing
 *   POST  /api/admin/venues/{id}/copy-to-client
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Share2, Copy, Users, AlertCircle, Loader2 } from "lucide-react";

// ============================================================
// Visibility badge
// ============================================================

const VIS_STYLES = {
  tenant_library: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  shared_with_clients: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  client_private: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  client_copy: "bg-violet-500/15 text-violet-200 border-violet-500/40",
};

export function VenueVisibilityBadge({ venue, clientsById = {} }) {
  if (!venue) return null;
  const sharedIds = Array.isArray(venue.shared_client_ids)
    ? venue.shared_client_ids.filter(Boolean)
    : [];
  const isCopy =
    venue.is_client_copy === true || Boolean(venue.copied_for_client_id);

  let kind = venue.visibility_type;
  if (!kind) {
    if (isCopy) kind = "client_copy";
    else if (sharedIds.length > 0) kind = "shared_with_clients";
    else if (venue.client_id) kind = "client_private";
    else kind = "tenant_library";
  }
  // Normalise legacy/client-side aliases.
  if (kind === "shared_with_client") kind = "shared_with_clients";
  if (kind === "client_owned") kind = "client_private";

  let label = "Tenant library";
  if (kind === "shared_with_clients") {
    label = `Shared with ${sharedIds.length} ${sharedIds.length === 1 ? "client" : "clients"}`;
  } else if (kind === "client_private") {
    const cname = clientsById[venue.client_id]?.name || "client";
    label = `Client private: ${cname}`;
  } else if (kind === "client_copy") {
    const cname =
      clientsById[venue.copied_for_client_id]?.name ||
      clientsById[venue.client_id]?.name ||
      "client";
    label = `Client copy: ${cname}`;
  }

  const cls =
    VIS_STYLES[kind] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}
      data-testid={`venue-visibility-badge-${venue.id}`}
      data-visibility-kind={kind}
      title={label}
    >
      {label}
    </span>
  );
}

// ============================================================
// Share dialog
// ============================================================

export function VenueShareDialog({ open, onOpenChange, venue, clients, onSaved }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && venue) {
      setSelectedIds(
        Array.isArray(venue.shared_client_ids)
          ? venue.shared_client_ids.filter(Boolean)
          : [],
      );
      setFilter("");
    }
  }, [open, venue]);

  const visibleClients = useMemo(() => {
    const all = clients || [];
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [clients, filter]);

  const toggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const save = async () => {
    if (!venue) return;
    setSaving(true);
    try {
      const r = await api.patch(
        `/venues/${venue.id}/client-sharing`,
        { shared_client_ids: selectedIds },
      );
      toast.success(
        selectedIds.length === 0
          ? "Sharing cleared"
          : `Shared with ${selectedIds.length} ${selectedIds.length === 1 ? "client" : "clients"}`,
      );
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save sharing");
    } finally {
      setSaving(false);
    }
  };

  if (!venue) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="venue-share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share venue with clients
          </DialogTitle>
          <DialogDescription>
            Adds <strong>{venue.name}</strong> to the selected clients' Client
            Portal. Sharing uses the <em>same</em> venue and location IDs —
            no copy is created. Unticking removes a client's access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            placeholder="Filter clients…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8"
            data-testid="venue-share-client-filter"
          />
          <div className="max-h-72 overflow-y-auto rounded border border-border/40 divide-y divide-border/40">
            {visibleClients.length === 0 && (
              <div className="p-3 text-xs italic text-muted-foreground">
                No clients match.
              </div>
            )}
            {visibleClients.map((c) => {
              const isOwner = venue.client_id === c.id;
              const checked = selectedIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-2 hover:bg-accent/30 cursor-pointer text-sm"
                  data-testid={`venue-share-row-${c.id}`}
                >
                  <Checkbox
                    checked={checked || isOwner}
                    disabled={isOwner || saving}
                    onCheckedChange={() => toggle(c.id)}
                    data-testid={`venue-share-check-${c.id}`}
                  />
                  <span className="flex-1">{c.name}</span>
                  {isOwner && (
                    <span className="text-[10px] uppercase tracking-wide text-amber-300">
                      owner
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {selectedIds.length} selected
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="venue-share-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="venue-share-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save sharing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Copy-to-client dialog
// ============================================================

export function VenueCopyToClientDialog({
  open, onOpenChange, venue, clients, locations, onCopied,
}) {
  const [targetClientId, setTargetClientId] = useState("");
  const [newName, setNewName] = useState("");
  const [notes, setNotes] = useState("");
  const [includeIds, setIncludeIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && venue) {
      setTargetClientId("");
      setNewName(venue.name || "");
      setNotes("");
      setIncludeIds((locations || []).map((l) => l.id));
      setFilter("");
      setResult(null);
    }
  }, [open, venue, locations]);

  const visibleClients = useMemo(() => {
    const all = (clients || []).filter((c) => c.id !== venue?.client_id);
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [clients, venue, filter]);

  const toggleLocation = (id) => {
    setIncludeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const submit = async () => {
    if (!venue || !targetClientId) {
      toast.error("Pick a target client first");
      return;
    }
    const totalLocs = (locations || []).length;
    if (totalLocs >= 20) {
      // Confirm before bulk copy
      if (!window.confirm(
        `This venue has ${totalLocs} active locations.\nA copy will create ${includeIds.length} new locations under the target client.\n\nContinue?`,
      )) return;
    }
    setSaving(true);
    try {
      const r = await api.post(
        `/admin/venues/${venue.id}/copy-to-client`,
        {
          target_client_id: targetClientId,
          new_name: newName.trim() ? newName.trim() : null,
          include_location_ids: includeIds,
          visibility_notes: notes.trim() ? notes.trim() : null,
        },
      );
      setResult(r.data);
      toast.success(
        `Copied: ${r.data.copied_location_count} location${r.data.copied_location_count === 1 ? "" : "s"}`,
      );
      onCopied?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copy failed");
    } finally {
      setSaving(false);
    }
  };

  if (!venue) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="venue-copy-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4" />
            Copy venue to client
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="block">
              Creates a <strong>new</strong> editable venue under the target
              client. The copied venue and its locations get new IDs. Editing
              the copy will <strong>not</strong> change <em>{venue.name}</em>.
              No pricing, rules, projects, or shifts are touched.
            </span>
            <span className="block text-amber-200 text-[11px]">
              Use this when the client needs venue-specific notes, meeting
              points, or naming that should not affect other clients.
            </span>
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Target client</Label>
              <Input
                placeholder="Filter clients…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-8 mb-1"
                data-testid="venue-copy-client-filter"
              />
              <div className="max-h-44 overflow-y-auto rounded border border-border/40 divide-y divide-border/40">
                {visibleClients.length === 0 && (
                  <div className="p-3 text-xs italic text-muted-foreground">
                    No eligible target clients.
                  </div>
                )}
                {visibleClients.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/30 cursor-pointer text-sm"
                    data-testid={`venue-copy-target-row-${c.id}`}
                  >
                    <input
                      type="radio"
                      name="venue-copy-target"
                      value={c.id}
                      checked={targetClientId === c.id}
                      onChange={() => setTargetClientId(c.id)}
                      disabled={saving}
                      data-testid={`venue-copy-target-${c.id}`}
                    />
                    <span className="flex-1">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Copied venue name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={255}
                placeholder={venue.name}
                className="h-8"
                data-testid="venue-copy-name"
              />
            </div>
            <div>
              <Label className="text-xs">
                Locations to include ({includeIds.length} of {(locations || []).length})
              </Label>
              <div className="max-h-40 overflow-y-auto rounded border border-border/40 divide-y divide-border/40">
                {(locations || []).length === 0 && (
                  <div className="p-3 text-xs italic text-muted-foreground">
                    No active locations under this venue.
                  </div>
                )}
                {(locations || []).map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/30 cursor-pointer text-xs"
                    data-testid={`venue-copy-loc-${l.id}`}
                  >
                    <Checkbox
                      checked={includeIds.includes(l.id)}
                      onCheckedChange={() => toggleLocation(l.id)}
                      disabled={saving}
                      data-testid={`venue-copy-loc-check-${l.id}`}
                    />
                    <span className="flex-1">{l.name || "(unnamed)"}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Internal notes (admin-only)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
                rows={2}
                placeholder="e.g. Reason for client-specific copy, special access instructions…"
                data-testid="venue-copy-notes"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="venue-copy-result">
            <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-emerald-300" />
                <strong>Copy created</strong>
              </div>
              <p className="text-xs text-muted-foreground">
                New venue ID: <code>{result.venue?.id}</code>
              </p>
              <p className="text-xs text-muted-foreground">
                Target client: <strong>{result.target_client_name}</strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Locations copied: <strong>{result.copied_location_count}</strong>
              </p>
            </div>
            <div className="flex items-start gap-2 text-[11px] text-amber-200">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
              <span>
                Source venue is unchanged. The copied venue is now editable
                under the target client.
              </span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="venue-copy-cancel"
          >
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={submit}
              disabled={saving || !targetClientId}
              data-testid="venue-copy-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Copy venue"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default {
  VenueVisibilityBadge,
  VenueShareDialog,
  VenueCopyToClientDialog,
};
